import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Search } from 'lucide-react'

import type {
    ShopAccountFilter,
    ShopAccountRow,
    ShopProvider,
} from '@/lib/api/shop-account-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { providerLabel } from '@/components/retail/shop-account-labels'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDebounced } from '@/hooks/use-debounced'
import { useShopAccounts } from '@/hooks/use-shop-account-query'
import { useShopTiers } from '@/hooks/use-shop-tier-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { RETAIL_MEMBER_TIERS_ENABLED } from '@/lib/retail-features'

// Bán lẻ › Tài khoản khách — tra cứu (wireframe ErpKhachBanLe). Web và Zalo
// Mini App là 2 bản ghi riêng (chưa gộp). Hạng / chi tiêu ở danh sách đọc cột
// cache (ghi lại mỗi đêm, hợp đồng §14.3.3.4); màn chi tiết đọc số trực tiếp.
export const Route = createFileRoute('/_app/_authed/_sidebar/retail/accounts/')(
    {
        component: AccountsPage,
    },
)

const PER_PAGE = 20

/** "true" / "false" / "all" của ô lọc liên kết → tham số API. */
export function linkedParam(v: string): boolean | undefined {
    if (v === 'all') return undefined
    return v === 'true'
}

export function AccountsPage() {
    const [q, setQ] = useState('')
    const [source, setSource] = useState('all')
    const [linked, setLinked] = useState('all')
    const [tier, setTier] = useState('all')
    // Hạng thành viên đang ẩn (ERP-07): không lọc, không cột, không gọi API hạng.
    const tiers =
        useShopTiers({ enabled: RETAIL_MEMBER_TIERS_ENABLED }).data?.data ?? []
    const [page, setPage] = useState(1)
    const search = useDebounced(q.trim(), 300)
    const filter: ShopAccountFilter = {
        q: search || undefined,
        source:
            source === 'all'
                ? undefined
                : (source as ShopAccountFilter['source']),
        linked: linkedParam(linked),
        tier_id: tier === 'all' ? undefined : tier,
        page,
        per_page: PER_PAGE,
    }
    const query = useShopAccounts(filter)
    const rows = query.data?.data ?? []
    const meta = query.data?.meta
    const totalPages = Math.max(meta?.total_pages ?? 1, 1)
    const reset =
        <T,>(set: (v: T) => void) =>
        (v: T) => {
            set(v)
            setPage(1)
        }

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_account_title()}
                subtitle={m.shop_account_subtitle()}
            />
            <div className="flex flex-col gap-3 md:flex-row md:items-end">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="pl-9"
                        value={q}
                        aria-label={m.shop_account_search()}
                        placeholder={m.shop_account_search()}
                        onChange={(e) => reset(setQ)(e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3 md:flex">
                    {RETAIL_MEMBER_TIERS_ENABLED && (
                        <div className="space-y-1.5 md:w-44">
                            <Label htmlFor="acc-tier">
                                {m.shop_account_col_tier()}
                            </Label>
                            <Select value={tier} onValueChange={reset(setTier)}>
                                <SelectTrigger id="acc-tier">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">
                                        {m.shop_all()}
                                    </SelectItem>
                                    {tiers.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div className="space-y-1.5 md:w-44">
                        <Label htmlFor="acc-source">
                            {m.shop_account_source()}
                        </Label>
                        <Select value={source} onValueChange={reset(setSource)}>
                            <SelectTrigger id="acc-source">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    {m.shop_all()}
                                </SelectItem>
                                <SelectItem value="web">
                                    {m.shop_account_source_web()}
                                </SelectItem>
                                <SelectItem value="zalo_mini_app">
                                    {m.shop_account_source_miniapp()}
                                </SelectItem>
                                <SelectItem value="both">
                                    {m.shop_account_source_both()}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5 md:w-44">
                        <Label htmlFor="acc-linked">
                            {m.shop_account_linked_filter()}
                        </Label>
                        <Select value={linked} onValueChange={reset(setLinked)}>
                            <SelectTrigger id="acc-linked">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    {m.shop_all()}
                                </SelectItem>
                                <SelectItem value="true">
                                    {m.shop_account_linked_yes()}
                                </SelectItem>
                                <SelectItem value="false">
                                    {m.shop_account_linked_no()}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                {m.shop_account_count({ n: String(meta?.total ?? 0) })}
            </p>
            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.shop_account_col_name()}</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>{m.shop_account_col_phone()}</TableHead>
                            <TableHead>{m.shop_account_col_web()}</TableHead>
                            <TableHead>{m.shop_account_col_zalo()}</TableHead>
                            {RETAIL_MEMBER_TIERS_ENABLED && (
                                <TableHead>
                                    {m.shop_account_col_tier()}
                                </TableHead>
                            )}
                            <TableHead className="text-right">
                                {m.shop_account_col_spend()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.shop_account_col_orders()}
                            </TableHead>
                            <TableHead>{m.shop_account_col_link()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isLoading ? (
                            <TableRow>
                                <TableCell colSpan={9}>
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : query.isError ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-destructive"
                                >
                                    {m.shop_account_load_failed()}
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="text-muted-foreground"
                                >
                                    {m.shop_account_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((a) => <AccountLine key={a.id} a={a} />)
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm italic text-muted-foreground">
                    {m.shop_account_orders_note()}
                </p>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                    >
                        {m.common_prev()}
                    </Button>
                    <span className="text-sm tabular-nums">
                        {page} / {totalPages}
                    </span>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={page >= totalPages}
                        onClick={() => setPage(page + 1)}
                    >
                        {m.common_next()}
                    </Button>
                </div>
            </div>
        </div>
    )
}

function ProviderChips({ list }: { list: ShopProvider[] }) {
    if (list.length === 0)
        return <span className="text-muted-foreground">—</span>
    return (
        <div className="flex flex-wrap gap-1">
            {list.map((p) => (
                <Badge key={p} variant="secondary">
                    {providerLabel(p)}
                </Badge>
            ))}
        </div>
    )
}

function AccountLine({ a }: { a: ShopAccountRow }) {
    const web = a.providers.filter((p) => p !== 'zalo')
    const zalo = a.providers.filter((p) => p === 'zalo')
    return (
        <TableRow>
            <TableCell>
                <Link
                    to="/retail/accounts/$accountId"
                    params={{ accountId: a.id }}
                    className="font-medium text-primary hover:underline"
                >
                    {a.name}
                </Link>
                {a.status === 'locked' && (
                    <Badge variant="destructive" className="ml-2">
                        {m.shop_account_status_locked()}
                    </Badge>
                )}
            </TableCell>
            <TableCell className="max-w-56 truncate">
                {a.email ?? '—'}
            </TableCell>
            <TableCell className="tabular-nums">{a.phone ?? '—'}</TableCell>
            <TableCell>
                <ProviderChips list={web} />
            </TableCell>
            <TableCell>
                <ProviderChips list={zalo} />
            </TableCell>
            {RETAIL_MEMBER_TIERS_ENABLED && (
                <TableCell>{a.tier_name ?? '—'}</TableCell>
            )}
            <TableCell className="text-right tabular-nums">
                {formatVnd(a.spend)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
                {a.orders}
            </TableCell>
            <TableCell>
                {a.customer_id ? (
                    <Link
                        to="/customers/$customerId"
                        search={{ tab: 'info' }}
                        params={{ customerId: a.customer_id }}
                        className="hover:underline"
                    >
                        {a.customer_code} · {a.customer_name}
                    </Link>
                ) : (
                    <span className="text-muted-foreground">
                        {m.shop_account_not_linked()}
                    </span>
                )}
            </TableCell>
        </TableRow>
    )
}
